import {
  GetValue,
  PartialScreenWithCoveragesRecord,
  ScreenWithCoverages,
} from "../types";
import {
  getHomeCoverageValueFormats,
  getHomeCoverageSettlementTypeValueFormats,
  getOtherStructuresValueFormats,
  getOtherStructuresSettlementTypeValueFormats,
  getYourBelongingsValueFormats,
  getYourBelongingsSettlementTypeValueFormats,
  getStandardDeductibleValueFormats,
  getWindHailValueFormats,
  getWaterDamageDeductibleValueFormats,
  getLossOfUseValueFormats,
  getWaterDamageValueFormats,
  getLiabilityValueFormats,
  getMedicalPaymentsValueFormats,
  getInflationGuardValueFormats,
  getRoofPaymentScheduleValueFormats,
} from "./decorators/valueFormats";
import {
  getHomeCoverageSettlementTypeTooltip,
  getOtherStructuresSettlementTypeTooltip,
  getYourBelongingsSettlementTypeTooltip,
} from "./options/settlementOption";
import { PackageCoverageAccessor } from "./packageCoverageAccessor";
import {
  GetPackageCoverageDetailsFactory,
  GetPackageCoverageGetters,
  GetPackageCoverageNameFactory,
  GetPackageCoverageValueFormats,
} from "./types";

const packageCoverageName: Record<PackageCoverageAccessor, string> = {
  [PackageCoverageAccessor.HomeCoverage]: "Home Coverage",
  [PackageCoverageAccessor.HomeCoverageSettlementType]:
    "Home Coverage Settlement Type",
  [PackageCoverageAccessor.YourBelongings]: "Your Belongings",
  [PackageCoverageAccessor.YourBelongingsSettlementType]:
    "Your Belongings Settlement Type",
  [PackageCoverageAccessor.OtherStructures]: "Other Structures",
  [PackageCoverageAccessor.OtherStructuresSettlementType]:
    "Other Structures Settlement Type",
  [PackageCoverageAccessor.StandardDeductible]: "Standard Deductible (AOP)",
  [PackageCoverageAccessor.WindHail]: "Wind/Hail Deductible",
  [PackageCoverageAccessor.WaterDamageDeductible]: "Water Damage Deductible",
  [PackageCoverageAccessor.LossOfUse]: "Loss of Use",
  [PackageCoverageAccessor.WaterDamage]: "Water Damage",
  [PackageCoverageAccessor.MedicalPayments]: "Medical Payments to Others",
  [PackageCoverageAccessor.PersonalLiability]: "Personal Liability",
  [PackageCoverageAccessor.PremisesLiability]: "Premises Liability",
  [PackageCoverageAccessor.InflationGuard]: "Inflation Guard",
  [PackageCoverageAccessor.RoofPaymentSchedule]: "Roof Payment Schedule",
};

const packageCoverageDescription: Record<
  PackageCoverageAccessor,
  PartialScreenWithCoveragesRecord
> = {
  [PackageCoverageAccessor.HomeCoverage]: {
    [ScreenWithCoverages.PlanDetails]:
      "Covers your home and any permanently attached structures to your home (e.g. steps, skirting, tie-downs). The limit is the maximum amount that will be paid in the event of a complete loss of your home. Does not include coverage for your belongings, liability, or other structures set apart from the home.",
  },
  [PackageCoverageAccessor.HomeCoverageSettlementType]: {
    [ScreenWithCoverages.PlanDetails]:
      "Settlement Type refers to how the worth of a damaged piece of property is determined. As an example, is a 15-year-old roof worth as much as a brand new roof, or is it worth less because it has been used for 15 years.",
  },
  [PackageCoverageAccessor.YourBelongings]: {
    [ScreenWithCoverages.PlanDetails]:
      "Covers damage to the stuff you own — furniture, electronics, and clothing, for example. This type of coverage helps pay to repair or replace your belongings after a covered loss, such as fire or windstorm.",
  },
  [PackageCoverageAccessor.YourBelongingsSettlementType]: {
    [ScreenWithCoverages.PlanDetails]:
      "Settlement Type refers to how the worth of a damaged piece of property is determined. As an example, is a 5-year-old oven worth as much as a brand new oven, or is it worth less because it has been used for 5 years.",
  },
  [PackageCoverageAccessor.OtherStructures]: {
    [ScreenWithCoverages.PlanDetails]:
      "This coverage helps pay to repair or replace structures other than your home, such as a fence or detached garage, if they are damaged by a covered peril.",
  },
  [PackageCoverageAccessor.OtherStructuresSettlementType]: {
    [ScreenWithCoverages.PlanDetails]:
      "Settlement Option refers to how the worth of a damaged piece of property is determined. As an example, is a 10-year-old fence worth as much as a brand new fence, or is it worth less because it has been used for 10 years.",
  },
  [PackageCoverageAccessor.StandardDeductible]: {
    [ScreenWithCoverages.PlanDetails]:
      "This All Other Perils (AOP) deductible is the amount of money you'll be required to pay for an eligible claim before your insurance plan starts to pay. Other deductibles may apply for specific coverages or perils (e.g. windstorm or earthquake). If $50,000 is the covered damage for a claim and you have a $1,000 deductible, you will be paid $49,000.",
  },
  [PackageCoverageAccessor.WindHail]: {
    [ScreenWithCoverages.PlanDetails]:
      "This amount will be deducted from any claim payment you receive for windstorm/hail claims.",
  },
  [PackageCoverageAccessor.WaterDamageDeductible]: {
    [ScreenWithCoverages.PlanDetails]:
      "This amount will be deducted from any claim payment you receive for water damage claims.",
  },
  [PackageCoverageAccessor.LossOfUse]: {
    [ScreenWithCoverages.PlanDetails]:
      "This coverage provides you with money that allows you to maintain your normal lifestyle and compensates you for the extra costs of living elsewhere when your home is damaged, destroyed, or uninhabitable.",
  },
  [PackageCoverageAccessor.WaterDamage]: {
    [ScreenWithCoverages.PlanDetails]:
      "Losses caused by accidental discharge, overflow of water or steam, pipe burst.",
  },
  [PackageCoverageAccessor.MedicalPayments]: {
    [ScreenWithCoverages.PlanDetails]:
      "Medical payments coverage can help to pay for expenses related to an injury that occurs on your property — whether you’ve been found liable or not.",
  },
  [PackageCoverageAccessor.PersonalLiability]: {
    [ScreenWithCoverages.PlanDetails]:
      'Personal Liability coverage protects you if a claim or suit is brought against you because of bodily injury or property damage to other people (or their stuff). If liabilityType is "Premises," coverage applies only as it relates to the insured property.',
  },
  [PackageCoverageAccessor.PremisesLiability]: {
    [ScreenWithCoverages.PlanDetails]:
      "Premises Liability coverage protects you if a claim or suit is brought against you because of bodily injury or property damage to other people (or their stuff) but only as it relates to the insured property.",
  },
  [PackageCoverageAccessor.InflationGuard]: {},
  [PackageCoverageAccessor.RoofPaymentSchedule]: {
    [ScreenWithCoverages.PlanDetails]:
      "Increases the affordability of insurance by reducing coverage based on the age and material of your roofing.",
  },
};

const packageCoverageTooltip: Record<
  Exclude<
    PackageCoverageAccessor,
    | PackageCoverageAccessor.HomeCoverageSettlementType
    | PackageCoverageAccessor.YourBelongingsSettlementType
    | PackageCoverageAccessor.OtherStructuresSettlementType
  >,
  PartialScreenWithCoveragesRecord
> = {
  [PackageCoverageAccessor.HomeCoverage]: {
    [ScreenWithCoverages.PolicySummary]:
      "Covers your home and any permanently attached structures to your home (e.g. steps, skirting, tie-downs). The limit is the maximum amount that will be paid in the event of a complete loss of your home. Does not include coverage for your belongings, liability, or other structures set apart from the home.",
  },
  [PackageCoverageAccessor.YourBelongings]: {
    [ScreenWithCoverages.PolicySummary]:
      "Covers damage to the stuff you own — furniture, electronics, and clothing, for example. The limit selected is the maximum amount that will be paid in the event of a complete loss of your belongings.",
  },
  [PackageCoverageAccessor.OtherStructures]: {
    [ScreenWithCoverages.PolicySummary]:
      "This coverage helps pay to repair or replace structures other than your home, such as a fence or detached garage, if they are damaged by a covered peril. The limit selected is the maximum amount that will be paid in the event of a complete loss of your other structures.",
  },
  [PackageCoverageAccessor.StandardDeductible]: {
    [ScreenWithCoverages.PolicySummary]:
      "This All Other Perils (AOP) deductible is the amount of money you'll be required to pay for an eligible claim before your insurance plan starts to pay. Other deductibles may apply for specific coverages or perils (e.g. windstorm or earthquake).",
  },
  [PackageCoverageAccessor.WindHail]: {
    [ScreenWithCoverages.PolicySummary]:
      "This amount will be deducted from any claim payment you receive for windstorm/hail claims.",
  },
  [PackageCoverageAccessor.WaterDamageDeductible]: {
    [ScreenWithCoverages.PolicySummary]:
      "This amount will be deducted from any claim payment you receive for water damage claims.",
  },
  [PackageCoverageAccessor.LossOfUse]: {
    [ScreenWithCoverages.PolicySummary]:
      "This coverage provides you with money that allows you to maintain your normal lifestyle and compensates you for the extra costs of living elsewhere when your home is damaged, destroyed, or uninhabitable.",
  },
  [PackageCoverageAccessor.WaterDamage]: {
    [ScreenWithCoverages.PolicySummary]:
      "Losses caused by accidental discharge, overflow of water or steam, pipe burst.",
  },
  [PackageCoverageAccessor.MedicalPayments]: {
    [ScreenWithCoverages.PolicySummary]:
      "Medical payments coverage can help to pay for expenses related to an injury to a guest that occurs on your property — whether you’ve been found liable or not.",
  },
  [PackageCoverageAccessor.PersonalLiability]: {
    [ScreenWithCoverages.PolicySummary]:
      "Personal Liability coverage protects you if a claim or suit is brought against you because of bodily injury or property damage to other people (or their stuff).",
  },
  [PackageCoverageAccessor.PremisesLiability]: {
    [ScreenWithCoverages.PolicySummary]:
      "Premises Liability coverage protects you if a claim or suit is brought against you because of bodily injury or property damage to other people (or their stuff) but only as it relates to the insured property.",
  },
  [PackageCoverageAccessor.InflationGuard]: {
    [ScreenWithCoverages.PolicySummary]:
      "This coverage automatically increases your policy’s dwelling coverage limit to account for rising construction costs. Increases to coverage and premium may occur at renewal time.",
  },
  [PackageCoverageAccessor.RoofPaymentSchedule]: {
    [ScreenWithCoverages.PolicySummary]:
      "Reduces coverage based on the age and material of roofing.",
  },
};

const packageCoverageValueFormats: Record<PackageCoverageAccessor, GetValue> = {
  [PackageCoverageAccessor.HomeCoverage]: getHomeCoverageValueFormats,
  [PackageCoverageAccessor.HomeCoverageSettlementType]:
    getHomeCoverageSettlementTypeValueFormats,
  [PackageCoverageAccessor.OtherStructures]: getOtherStructuresValueFormats,
  [PackageCoverageAccessor.OtherStructuresSettlementType]:
    getOtherStructuresSettlementTypeValueFormats,
  [PackageCoverageAccessor.YourBelongings]: getYourBelongingsValueFormats,
  [PackageCoverageAccessor.YourBelongingsSettlementType]:
    getYourBelongingsSettlementTypeValueFormats,
  [PackageCoverageAccessor.StandardDeductible]:
    getStandardDeductibleValueFormats,
  [PackageCoverageAccessor.WindHail]: getWindHailValueFormats,
  [PackageCoverageAccessor.WaterDamageDeductible]:
    getWaterDamageDeductibleValueFormats,
  [PackageCoverageAccessor.LossOfUse]: getLossOfUseValueFormats,
  [PackageCoverageAccessor.WaterDamage]: getWaterDamageValueFormats,
  [PackageCoverageAccessor.PersonalLiability]: getLiabilityValueFormats,
  [PackageCoverageAccessor.PremisesLiability]: getLiabilityValueFormats,
  [PackageCoverageAccessor.MedicalPayments]: getMedicalPaymentsValueFormats,
  [PackageCoverageAccessor.InflationGuard]: getInflationGuardValueFormats,
  [PackageCoverageAccessor.RoofPaymentSchedule]:
    getRoofPaymentScheduleValueFormats,
};

const getPackageCoverageName: GetPackageCoverageNameFactory =
  (accessor) => () => {
    return packageCoverageName[accessor];
  };

const getPackageCoverageTooltip: GetPackageCoverageDetailsFactory =
  (accessor, screen) =>
  (options = {}) => {
    const { settlementOption } = options;

    if (accessor === PackageCoverageAccessor.HomeCoverageSettlementType) {
      return settlementOption
        ? getHomeCoverageSettlementTypeTooltip(settlementOption, screen)
        : undefined;
    }

    if (accessor === PackageCoverageAccessor.YourBelongingsSettlementType) {
      return settlementOption
        ? getYourBelongingsSettlementTypeTooltip(settlementOption, screen)
        : undefined;
    }

    if (accessor === PackageCoverageAccessor.OtherStructuresSettlementType) {
      return settlementOption
        ? getOtherStructuresSettlementTypeTooltip(settlementOption, screen)
        : settlementOption;
    }

    return packageCoverageTooltip[accessor][screen];
  };

const getPackageCoverageDescription: GetPackageCoverageDetailsFactory =
  (accessor, screen) => () => {
    return packageCoverageDescription[accessor][screen];
  };

const getPackageCoverageValueFormats: GetPackageCoverageValueFormats = (
  accessor
) => {
  return packageCoverageValueFormats[accessor];
};

const getPackageCoverageGetters: GetPackageCoverageGetters = (
  accessor,
  screen
) => ({
  getName: getPackageCoverageName(accessor),
  getValueFormats: getPackageCoverageValueFormats(accessor),
  getDescription: getPackageCoverageDescription(accessor, screen),
  getTooltip: getPackageCoverageTooltip(accessor, screen),
});

export {
  getPackageCoverageGetters,
  packageCoverageName,
  packageCoverageDescription,
  packageCoverageTooltip,
  packageCoverageValueFormats,
};
